import React from 'react';
import {
  Wrapper,
  Header,
  HeaderLabel,
  Content,
  IconWrapper,
  GridRow,
  GridRowPlain,
  Cell,
  CellNumber,
  CellIcon,
  Label,
  AllocationDot,
  AllocationCount,
} from './DimensionGroup.styles';
import { IAllocatedDimensionGroup, IDimensionSelection } from '../types';
import { ApIcon, colors } from '@alixpartners/ui-components';
import { getFormattedCurrency } from 'utils/format';
import { ICurrencyFormat } from 'utils/types';

interface IProps {
  data: IAllocatedDimensionGroup;
  onSelect(id: IDimensionSelection): void;
  onExpand(id: string): void;
  isExpanded?: boolean;
  disableGroupSelection?: boolean;
  selection: IDimensionSelection | null;
  format: ICurrencyFormat;
}

export const DimensionGroup: React.FC<IProps> = ({
  isExpanded,
  disableGroupSelection,
  format,
  onSelect,
  onExpand,
  selection,
  data,
}) => {
  const toggle = (e: any) => {
    e.stopPropagation();

    onExpand(data.id);
  };

  const select = (item: IDimensionSelection) => () => {
    onSelect(item);
  };

  const formattedSpend = getFormattedCurrency({
    value: data.spend,
    currencyFormat: format,
    isUnitSign: true,
    isCurrencySign: true,
    isIdealDenomination: true,
  });

  if (!data.children || data.children.length === 0) {
    return (
      <Header>
        <GridRowPlain
          isSelected={!!selection && data.id === selection.id}
          onClick={select({ id: data.id })}
        >
          <Cell>
            <HeaderLabel title={data.name}>{data.name}</HeaderLabel>
          </Cell>
          <CellNumber>{formattedSpend}</CellNumber>

          <CellIcon>
            {data.isAllocated && (
              <AllocationDot isInvalid={data.isInvalid} large />
            )}
          </CellIcon>
        </GridRowPlain>
      </Header>
    );
  }

  const allocatedItems = data.children.filter(
    (item) => item.isAllocated
  ).length;
  const itemsCount = data.children.length;
  const isGroupInvalid = data.children.some((item) => item.isInvalid);
  const isParentInactive =
    disableGroupSelection || data.children.some((item) => item.isAllocated);
  const isChildInactive = data.isAllocated;

  return (
    <Wrapper>
      <Header>
        <GridRow
          isSelected={!!selection && data.id === selection.id}
          onClick={!isParentInactive ? select({ id: data.id }) : undefined}
          inactive={isParentInactive}
        >
          <IconWrapper isExpanded={isExpanded} onClick={toggle}>
            <ApIcon
              iconName="baseline_keyboard_arrow_right"
              iconColor={colors.grey8}
              iconSize={20}
            />
          </IconWrapper>
          <Cell>
            <HeaderLabel title={data.name}>{data.name}</HeaderLabel>
          </Cell>
          <CellNumber>{formattedSpend}</CellNumber>

          {data.isAllocated ? (
            <CellIcon>
              <AllocationDot isInvalid={data.isInvalid} large />
            </CellIcon>
          ) : (
            <CellNumber>
              <AllocationCount
                isAllocated={allocatedItems > 0}
                isInvalid={isGroupInvalid}
              >
                {allocatedItems}/{itemsCount}
              </AllocationCount>
            </CellNumber>
          )}
        </GridRow>
      </Header>

      {isExpanded && (
        <Content>
          {data.children.map((item) => (
            <GridRow
              key={item.id}
              isSelected={!!selection && item.id === selection.id}
              inactive={isChildInactive}
              onClick={
                !isChildInactive
                  ? select({ rootId: data.id, id: item.id })
                  : undefined
              }
            >
              <Cell />
              <Cell>
                <Label title={item.name}>{item.name}</Label>
              </Cell>
              <CellNumber>
                {getFormattedCurrency({
                  value: item.spend,
                  currencyFormat: format,
                  isUnitSign: true,
                  isCurrencySign: true,
                  isIdealDenomination: true,
                })}
              </CellNumber>
              <CellIcon>
                {item.isAllocated && (
                  <AllocationDot isInvalid={item.isInvalid} />
                )}
              </CellIcon>
            </GridRow>
          ))}
        </Content>
      )}
    </Wrapper>
  );
};
